import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { HeartHandshake, Plus, Archive, Mail, Users, Megaphone, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * OCI V2.7B — Charity Management Centre.
 * Agency-owned charity clients (visibility entities, NOT tenants).
 * Reuses the existing invite pipeline; no new onboarding/invite system.
 */
export const CharityManagementCentre = () => {
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", charityNumber: "" });
  const [detail, setDetail] = useState(null); // {charity, campaigns, users}
  const [inviteFor, setInviteFor] = useState(null);
  const [invite, setInvite] = useState({ email: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/charities?filter=active");
      setCharities(res.data || []);
    } catch (e) {
      toast.error("Failed to load charities");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitCharity = async () => {
    if (!form.name.trim()) { toast.error("Charity name is required"); return; }
    try {
      if (editing) {
        await apiClient.put(`/charities/${editing}`, { name: form.name, charityNumber: form.charityNumber || null });
        toast.success("Charity updated");
      } else {
        await apiClient.post("/charities", { name: form.name, charityNumber: form.charityNumber || null });
        toast.success("Charity created");
      }
      setShowCreate(false); setEditing(null); setForm({ name: "", charityNumber: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this charity? Its campaigns remain unchanged.")) return;
    try { await apiClient.post(`/charities/${id}/archive`); toast.success("Charity archived"); load(); }
    catch (e) { toast.error("Couldn't archive the record. Please try again."); }
  };

  const openDetail = async (ch) => {
    try {
      const [camps, users] = await Promise.all([
        apiClient.get(`/charities/${ch.id}/campaigns`),
        apiClient.get(`/charities/${ch.id}/users`),
      ]);
      setDetail({ charity: ch, campaigns: camps.data || [], users: users.data || [] });
    } catch (e) { toast.error("Failed to load charity detail"); }
  };

  const sendInvite = async () => {
    if (!invite.email.includes("@") || !invite.name.trim()) { toast.error("Name and valid email required"); return; }
    try {
      await apiClient.post(`/charities/${inviteFor.id}/invite`, { email: invite.email, name: invite.name });
      toast.success(`Invite sent to ${invite.email}`);
      setInviteFor(null); setInvite({ email: "", name: "" });
      if (detail?.charity?.id === inviteFor.id) openDetail(inviteFor);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="charity-management-centre">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <HeartHandshake className="h-5 w-5 text-rose-500" /> Charity Clients
          </h2>
          <p className="text-sm text-slate-500">Charities your campaigns serve. Visibility clients — not tenants.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: "", charityNumber: "" }); setShowCreate(true); }}
          className="bg-rose-600 hover:bg-rose-700" data-testid="create-charity-btn">
          <Plus className="h-4 w-4 mr-2" /> New Charity
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : charities.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-500">
          No charities yet. Create one, then link it to a campaign.
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {charities.map((ch) => (
            <Card key={ch.id} className="border-slate-200" data-testid={`charity-card-${ch.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="truncate">{ch.name}</span>
                  <Badge variant="outline" className="text-xs">{ch.status}</Badge>
                </CardTitle>
                {ch.charityNumber && <p className="text-xs text-slate-400">No. {ch.charityNumber}</p>}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> {ch.campaignCount} campaigns</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ch.userCount} users</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openDetail(ch)} data-testid={`charity-view-${ch.id}`}>View</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(ch.id); setForm({ name: ch.name, charityNumber: ch.charityNumber || "" }); setShowCreate(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setInviteFor(ch)} data-testid={`charity-invite-${ch.id}`}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> Invite
                  </Button>
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-rose-600" onClick={() => archive(ch.id)}>
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="charity-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Charity" : "New Charity"}</DialogTitle>
            <DialogDescription>Charities are visibility clients of your campaigns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Charity Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., British Heart Foundation" className="mt-1.5" data-testid="charity-name-input" />
            </div>
            <div>
              <Label>Charity Number</Label>
              <Input value={form.charityNumber} onChange={(e) => setForm((p) => ({ ...p, charityNumber: e.target.value }))}
                placeholder="Optional" className="mt-1.5" data-testid="charity-number-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={submitCharity} className="bg-rose-600 hover:bg-rose-700" data-testid="charity-save-btn">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="charity-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.charity?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Campaigns</h4>
                {detail.campaigns.length === 0 ? <p className="text-sm text-slate-400">No campaigns linked.</p> : (
                  <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.campaigns.map((c) => (
                      <TableRow key={c.id}><TableCell>{c.name}</TableCell><TableCell>{c.active ? "Yes" : "No"}</TableCell></TableRow>
                    ))}</TableBody></Table>
                )}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Charity Users</h4>
                {detail.users.length === 0 ? <p className="text-sm text-slate-400">No charity users yet.</p> : (
                  <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.users.map((u) => (
                      <TableRow key={u.id}><TableCell>{u.name}</TableCell><TableCell>{u.email}</TableCell></TableRow>
                    ))}</TableBody></Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={!!inviteFor} onOpenChange={() => setInviteFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="charity-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite charity user</DialogTitle>
            <DialogDescription>{inviteFor?.name} — they'll get a secure invite to set a password and view this charity's campaigns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Contact Name *</Label>
              <Input value={invite.name} onChange={(e) => setInvite((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Head of Fundraising" className="mt-1.5" data-testid="charity-invite-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input value={invite.email} onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
                placeholder="contact@charity.org" className="mt-1.5" data-testid="charity-invite-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteFor(null)}>Cancel</Button>
            <Button onClick={sendInvite} className="bg-rose-600 hover:bg-rose-700" data-testid="charity-invite-send">Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CharityManagementCentre;
